import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, Clock, XCircle, RefreshCw, Radio, TrendingUp } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pas`;

const StatCard = ({ label, value, tone, icon }) => (
  <div className={`flex-1 min-w-[120px] rounded-xl border p-4 ${tone}`} data-testid={`recon-stat-${label.toLowerCase()}`}>
    <div className="flex items-center gap-2 text-xs opacity-80">{icon} {label}</div>
    <p className="text-3xl font-bold mt-1">{value}</p>
  </div>
);

const StuckRow = ({ t, token, reload }) => {
  const [url, setUrl] = useState('');
  const repoll = async () => {
    try { const r = await axios.post(`${API}/bank-transmissions/${t.id}/repoll`, { status_url: url }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(r.data.success ? `${t.id}: polled (${r.data.poll.status_code})` : `Poll failed: ${r.data.poll.error || ''}`); toast.message(r.data.note); reload(); }
    catch (e) { toast.error(e.response?.data?.detail?.toString() || 'Failed'); }
  };
  return (
    <tr className="border-t border-slate-700" data-testid={`recon-stuck-${t.id}`}>
      <td className="py-1.5 font-mono text-[11px]">{t.id}</td>
      <td>{t.operation}</td>
      <td className="text-amber-300">{t.status}</td>
      <td className="text-slate-500 text-[11px]">{t.created_at ? new Date(t.created_at).toLocaleString() : ''}</td>
      <td>
        <div className="flex gap-1">
          <input placeholder="bank status URL" value={url} onChange={(e) => setUrl(e.target.value)} className="px-2 py-1 bg-slate-950 border border-slate-600 rounded text-white text-[11px] w-40" data-testid={`recon-polurl-${t.id}`} />
          <button disabled={!/^https?:\/\/.+/.test(url)} onClick={repoll} className="px-2 py-1 bg-cyan-700 hover:bg-cyan-600 disabled:opacity-40 text-white rounded text-[11px] flex items-center gap-1" data-testid={`recon-repoll-${t.id}`}><Radio className="w-3 h-3" /> Re-poll</button>
        </div>
      </td>
    </tr>
  );
};

export const BankReconciliation = ({ token }) => {
  const [s, setS] = useState(null);
  const [trends, setTrends] = useState([]);
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const load = useCallback(() => {
    const hh = { headers: { Authorization: `Bearer ${token}` } };
    axios.get(`${API}/bank-reconciliation/summary`, hh).then(r => setS(r.data)).catch(() => {});
    axios.get(`${API}/bank-reconciliation/trends?days=14`, hh).then(r => setTrends(r.data.series)).catch(() => {});
  }, [token]);
  useEffect(() => { load(); }, [load]);
  const runJob = async () => { try { const r = await axios.post(`${API}/bank-reconciliation/run`, {}, h); toast.success(r.data.message); load(); } catch { toast.error('Failed'); } };

  const totals = s?.totals || { settled: 0, awaiting: 0, failed: 0, all: 0 };
  return (
    <div className="bg-slate-800 border border-emerald-500/30 rounded-xl p-4 space-y-4" data-testid="bank-reconciliation">
      <div className="flex items-center justify-between">
        <div><p className="text-white font-semibold">Settlement reconciliation</p>
          <p className="text-xs text-slate-400 mt-1">Settlement is confirmed only by a verified bank event. Re-polling asks the bank for status; it does not change a transmission on its own.</p></div>
        <button onClick={runJob} className="px-3 py-1.5 bg-slate-600 hover:bg-slate-500 text-white rounded text-sm flex items-center gap-1" data-testid="recon-run"><RefreshCw className="w-4 h-4" /> Run reconciliation</button>
      </div>
      <div className="flex flex-wrap gap-3">
        <StatCard label="Settled" value={totals.settled} tone="bg-emerald-500/10 border-emerald-500/40 text-emerald-200" icon={<CheckCircle2 className="w-3.5 h-3.5" />} />
        <StatCard label="Awaiting" value={totals.awaiting} tone="bg-amber-500/10 border-amber-500/40 text-amber-200" icon={<Clock className="w-3.5 h-3.5" />} />
        <StatCard label="Failed" value={totals.failed} tone="bg-red-500/10 border-red-500/40 text-red-200" icon={<XCircle className="w-3.5 h-3.5" />} />
        <StatCard label="Total" value={totals.all} tone="bg-slate-700/40 border-slate-600 text-slate-200" icon={<Radio className="w-3.5 h-3.5" />} />
      </div>
      {s?.status_counts && Object.keys(s.status_counts).length > 0 && (
        <div className="flex flex-wrap gap-2 text-[11px]">{Object.entries(s.status_counts).map(([k, v]) => <span key={k} className="px-2 py-1 bg-slate-900 border border-slate-700 rounded text-slate-300">{k}: <b>{v}</b></span>)}</div>
      )}
      <div>
        <p className="text-sm text-white font-medium mb-1 flex items-center gap-1"><TrendingUp className="w-4 h-4 text-emerald-400" /> Settlement trend (last 14 days)</p>
        {trends.length ? (
          <div style={{ width: '100%', height: 220 }} data-testid="recon-trend-chart">
            <ResponsiveContainer>
              <BarChart data={trends} margin={{ top: 8, right: 12, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis allowDecimals={false} tick={{ fill: '#94a3b8', fontSize: 10 }} />
                <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="settled" stackId="a" fill="#10b981" name="Settled" radius={[2, 2, 0, 0]} />
                <Bar dataKey="pending" stackId="a" fill="#f59e0b" name="Awaiting" />
                <Bar dataKey="failed" stackId="a" fill="#ef4444" name="Failed" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : <p className="text-xs text-slate-500">No transmissions yet to chart.</p>}
      </div>
      <div>
        <p className="text-sm text-white font-medium mb-1">Stuck transmissions ({s?.stuck?.length || 0})</p>
        {s?.stuck?.length ? (
          <div className="overflow-x-auto max-h-80"><table className="w-full text-xs text-slate-300" data-testid="recon-stuck-table">
            <thead><tr className="text-left text-[11px] text-slate-500"><th>ID</th><th>Operation</th><th>Status</th><th>Sent</th><th>Re-poll</th></tr></thead>
            <tbody>{s.stuck.map(t => <StuckRow key={t.id} t={t} token={token} reload={load} />)}</tbody>
          </table></div>
        ) : <p className="text-xs text-slate-500">No stuck transmissions — all transmissions are settled or failed.</p>}
      </div>
    </div>
  );
};
